import type { ReactNode } from 'react';
import { Box, Button, Group, Stack, Text, Title, type ButtonProps } from '@mantine/core';

/**
 * A titled part of a settings tab: what it's for in a line, then its rows. Space, not rules, keeps parts apart.
 *
 * Where buttons go. Actions on the whole part (Sync now, Sign out, Export, a test) are an `actions` row under the
 * lead: left-aligned, compact (`ActionButton`), never taller than the title. A button that submits fields sits below
 * those fields at normal size, filled: the one prominent button in a part is the one that commits something.
 * The heading's `right` is for indicators only (a status badge).
 */
export const Part = ({ title, lead, right, actions, children, id, testId }: { title: ReactNode; lead?: ReactNode; right?: ReactNode; actions?: ReactNode; children?: ReactNode; id?: string; testId?: string }) => (
  <Stack gap="lg" id={id} style={{ scrollMarginTop: 12 }} data-testid={testId}>
    <Box>
      <Group justify="space-between" align="center" wrap="nowrap" gap="md">
        <Title order={2} size="h6">{title}</Title>
        {right}
      </Group>
      {lead && <Text size="sm" c="dimmed" mt={4} maw={720}>{lead}</Text>}
      {actions && <Group gap="xs" mt="sm">{actions}</Group>}
    </Box>
    {children}
  </Stack>
);

/** A part-level action: compact, quiet by default. `primary` for the one that matters most; `danger` for the destructive one. */
export const ActionButton = ({ primary, danger, ...props }: ButtonProps & { primary?: boolean; danger?: boolean; onClick?: () => void; 'data-testid'?: string }) => (
  <Button size="compact-sm" variant={primary ? 'filled' : danger ? 'subtle' : 'default'} color={danger ? 'red' : undefined} {...props} />
);

/** One setting: what it is and what it does on the left, the control on the right. */
export const Row = ({ label, help, children, id }: { label: ReactNode; help?: ReactNode; children?: ReactNode; id?: string }) => (
  <Group id={id} justify="space-between" align="flex-start" wrap="nowrap" gap="xl" style={{ scrollMarginTop: 12 }}>
    <Box style={{ minWidth: 0, flex: 1 }}>
      <Text size="sm" fw={500}>{label}</Text>
      {help && <Text size="sm" c="dimmed" mt={2}>{help}</Text>}
    </Box>
    {children && <Box style={{ flexShrink: 0 }}>{children}</Box>}
  </Group>
);

/** The gap between parts of a tab. */
export const PART_GAP = 40;
